import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useInvertedCursor } from '../../hooks/useInvertedCursor';
import { SEO } from '../../components/SEO';
import './RefundPolicy.css';

const FadeInSection = ({ children, className = '', delay = '' }) => {
    const [ref, isVisible] = useIntersectionObserver({ triggerOnce: true, threshold: 0.1 });
    return (
        <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className} ${delay}`}>
            {children}
        </div>
    );
};

const RefundPolicy = () => {
    useInvertedCursor();
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <SEO 
                title="Refund Policy | MAD OVER TECHNOLOGY"
                description="Refund Policy for madovertech.io. Learn about our refund policy for digital products and services."
                keywords="Refund Policy, Returns, Refunds, MAD OVER TECHNOLOGY"
                url="/refund-policy"
            />
            <main className="legal-page">
                <section className="legal-hero">
                    <div className="container mx-auto px-4">
                        <FadeInSection>
                            <h1 className="legal-title">Refund Policy</h1>
                            <p className="legal-subtitle">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </FadeInSection>
                    </div>
                </section>

                <section className="legal-content">
                    <div className="container mx-auto px-4 max-w-4xl">
                        <FadeInSection>
                            <div className="legal-text">
                                <p>At MAD OVER TECHNOLOGY, we offer digital products and services.</p>
                                
                                <p>Because these are instantly accessible and non-returnable, we follow this Refund Policy:</p>

                                <h2>1. Digital Products & Subscriptions</h2>
                                <p>Refunds are provided only in the following situations:</p>
                                <ul>
                                    <li>You were charged by mistake (duplicate payment)</li>
                                    <li>The product/service did not work as described and we are unable to fix it</li>
                                    <li>You experience a technical issue that prevents usage after debugging attempts</li>
                                </ul>
                                <p>Refund requests must be made within 7 days of purchase.</p>

                                <h2>2. When Refunds Are Not Provided</h2>
                                <p>We do not offer refunds for:</p>
                                <ul>
                                    <li>Change of mind</li>
                                    <li>Incorrect purchase</li>
                                    <li>"I didn't like it" or subjective dissatisfaction</li>
                                    <li>Failure to use the service</li>
                                    <li>Refund requested after subscription renewal</li>
                                </ul>

                                <h2>3. Subscription Cancellations</h2>
                                <p>You may cancel a subscription at any time.</p>
                                <ul>
                                    <li>The cancellation prevents future charges.</li>
                                    <li>Already-paid billing periods are not refundable.</li>
                                </ul>

                                <h2>4. Processing Refunds</h2>
                                <p>If approved, refunds are processed through Paddle to your original payment method.</p>
                                <p>Processing may take 3–10 business days depending on your bank.</p>

                                <h2>5. Contact for Refunds</h2>
                                <p>Email: <a href="mailto:hello@madovertech.io">hello@madovertech.io</a></p>
                                <p>Include your order ID and reason for refund.</p>
                            </div>
                        </FadeInSection>
                    </div>
                </section>

                {/* Footer */}
                <footer className="footer">
                    <div className="footer-links">
                        <Link to="/">Home</Link>
                        <Link to="/ai-photobooth">AI Photo Booth</Link>
                        <a href="https://nexasync.io" target="_blank" rel="noopener noreferrer">NexaSync</a>
                        <Link to="/terms-of-service">Terms of Service</Link>
                        <Link to="/privacy-policy">Privacy Policy</Link>
                        <Link to="/refund-policy">Refund Policy</Link>
                        <a href="https://www.instagram.com/madovertechnology" target="_blank" rel="noopener noreferrer">Instagram</a>
                        <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20services" target="_blank" rel="noopener noreferrer">Contact</a>
                    </div>
                    <div className="footer-locations">
                        <span>DELHI | BENGALORE | MUMBAI</span>
                    </div>
                    <div className="footer-contact">
                        <a href="mailto:hello@madovertech.io" className="footer-email">hello@madovertech.io</a>
                        <span className="footer-separator">|</span>
                        <a href="tel:+919711689861" className="footer-phone">+91 9711689861</a>
                    </div>
                    <p className="footer-copyright">© 2025 Mad Over Technology. All rights reserved.</p>
                </footer>
            </main>
        </>
    );
};

export default RefundPolicy;

